import { ThrowableStatus } from "@wss/error-tracking/throwable";

const defaultStatusCodes = [
  // request timeout
  408,
  // internal server error
  502,
  // service unavailable
  503,
  // gateway timeout
  504,
];

/**
 * Invoke a function that makes a HTTP request and returns a ThrowableStatus, retrying up to a
 * specified number of times.
 *
 * Note that only the retryable portion of the request should be included inside the loop (normally
 * the call to fetch itself and any per-request setup code)
 *
 * @remarks
 *
 * Retry loops are good for hiding recoverable errors on endpoints where there is no consequence for
 * repeat submissions, including readonly operations or stateless operations where the same operation
 * with the same arguments always results in the same outcome.  Care should be used adding retry
 * behavior to endpoints that change the server-side state (cart, user info)
 *
 * When using a retry loop with codes outside the default list (e.g. 200, 500) you should double check
 * backend code to verify you aren't duplicating and amplifying existing Polly retry behavior
 *
 * @param retryableFunction A function to execute the retryable HTTP request.
 * @param retries The number of times to retry the request. At least one attempt will be made.
 * @param delay The time to wait before retry, in ms
 * @param statusCodes The list of acceptable status codes to retry. Default: 408, 502, 503, 504
 * @returns The response from the http request if we get a result outside the retry list before
 * reaching the maximum number of tries. Otherwise, a ThrowableError containing the details of the
 * last error.
 */
const throwableHttpRetry = async <R = void, S = number>(
  retryableFunction: () => Promise<ThrowableStatus<R, S>>,
  retries = 2,
  delay = 200,
  statusCodes: Array<S | number> = defaultStatusCodes
): Promise<ThrowableStatus<R, S>> => {
  let lastResult: ThrowableStatus<R, S> | undefined;

  let timesTried = 0;
  let shouldRetry = true;

  do {
    timesTried += 1;

    // Intentional use of await within the retry loop to enforce a maximum number of tries
    // eslint-disable-next-line no-await-in-loop
    lastResult = await retryableFunction();

    // return the result for success, error results without a HTTP status, or status
    // codes not in the list of codes to retry, else loop and retry
    if (
      !lastResult.isError ||
      !lastResult.status ||
      !statusCodes.includes(lastResult.status)
    ) {
      return lastResult;
    }

    shouldRetry = timesTried <= retries;

    if (shouldRetry) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  } while (shouldRetry);

  // if we exceed the max number of retries, wrap the final error and return.
  return {
    isError: true,
    error: new Error(
      `Exceeded max number of retries (${retries}).  Last status: ${lastResult.status}.`,
      {
        cause: lastResult.error,
      }
    ),
    status: lastResult.status,
  };
};

export default throwableHttpRetry;
